// Tenant regions come from /tenant-information as a country prefix with an
// optional deployment suffix (UK, UK-1, IE-2, ...). Only the prefix changes
// app behaviour. Anything unrecognised (including undefined while the
// bootstrap request is in flight) resolves to UK.
export const resolveRegion = (region) =>
  String(region || '')
    .toUpperCase()
    .split('-')[0] === 'IE'
    ? 'IE'
    : 'UK'

export const isIrishRegion = (region) => resolveRegion(region) === 'IE'

// Both regions accept UK and Irish mobiles: crews and patients regularly
// cross the border, and the server delivers SMS to +44 and +353 either way.
export const MOBILE_LOCALES = ['en-GB', 'en-IE']

export const MOBILE_HELPER_TEXT = 'Must be a UK or Irish mobile number'

export const standardVatMultiplier = (region) =>
  isIrishRegion(region) ? 1.23 : 1.2

// Ireland's most commonly-relevant reduced rate (13.5%) vs the UK's (5%).
// Ireland also has a 9% tourism/hospitality rate, deliberately not modelled
// here — add it if a customer actually needs it.
export const reducedVatMultiplier = (region) =>
  isIrishRegion(region) ? 1.135 : 1.05

export const currencySymbol = (region) => (isIrishRegion(region) ? '€' : '£')

// Maps a stored line-item vat value to its multiplier for this tenant's
// region. Handles the current keyword scheme (none/standard/reduced) and
// legacy numeric-string values from before VAT was resolved by keyword.
export const vatMultiplierForItem = (vat, region) => {
  if (vat === 'standard') return standardVatMultiplier(region)
  if (vat === 'reduced') return reducedVatMultiplier(region)
  if (vat === 'none' || !vat) return 1
  const parsed = parseFloat(vat)
  return isNaN(parsed) ? 1 : 1 + parsed
}

const asPercent = (multiplier) => Math.round((multiplier - 1) * 100)

// Human label for a stored vat value, for display purposes.
export const vatLabel = (vat, region) => {
  if (vat === 'standard') return `Standard (${asPercent(standardVatMultiplier(region))}%)`
  if (vat === 'reduced') return `Reduced (${asPercent(reducedVatMultiplier(region))}%)`
  if (vat === 'none' || !vat) return 'No VAT / Exempt'
  const parsed = parseFloat(vat)
  return isNaN(parsed) ? String(vat) : `${Math.round(parsed * 100)}%`
}

// Dropdown options for editing a line item's VAT category, region-aware.
export const vatOptions = (region) => [
  { value: 'none', label: 'No VAT / Exempt' },
  { value: 'reduced', label: vatLabel('reduced', region) },
  { value: 'standard', label: vatLabel('standard', region) },
]

const LABELS = {
  UK: {
    nationalIdLabel: 'NHS Number',
    hospitalNumberLabel: 'Hospital Number',
    hospitalNumberNoun: 'Hospital',
    postCodeLabel: 'Post Code',
    niNumberLabel: 'National Insurance Number',
    nhsAmbulanceLabel: 'NHS Ambulance',
    ambulanceOrgLabel: 'Company / NHS Trust',
  },
  IE: {
    nationalIdLabel: 'IHI Number',
    hospitalNumberLabel: 'MRN',
    hospitalNumberNoun: 'Medical Record',
    postCodeLabel: 'Eircode',
    niNumberLabel: 'PPS Number',
    nhsAmbulanceLabel: 'National Ambulance Service',
    ambulanceOrgLabel: 'Company / HSE Service',
  },
}

export const regionLabels = (region) => LABELS[resolveRegion(region)]

// The regional deployment domains, for showing which country a host belongs
// to (e.g. the login page's country indicator). `code` matches the bundled
// flag assets in assets/images/flags/.
const DOMAIN_COUNTRIES = [
  { domain: 'ambunet.co.uk', code: 'gb', name: 'United Kingdom' },
  { domain: 'ambunet.ie', code: 'ie', name: 'Ireland' },
  { domain: 'ambunet.wales', code: 'gb-wls', name: 'Wales' },
  { domain: 'ambunet.de', code: 'de', name: 'Germany' },
  { domain: 'ambu.net', code: 'eu', name: 'Europe' },
]

// Unlike resolveRegion, unknown hosts return null (NOT a UK fallback) so
// callers hide the indicator on localhost/dev and unrecognised domains.
export const countryForHostname = (hostname) =>
  DOMAIN_COUNTRIES.find(
    (c) => hostname === c.domain || hostname.endsWith('.' + c.domain),
  ) || null
